import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LogOut, Users, Activity, Calendar, Dumbbell, Clock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const TrainerDashboard = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/trainer/dashboard');
      setDashboardData(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
      if (error.response?.status === 403) {
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
    toast.success('Logged out successfully');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-foreground">Loading trainer dashboard...</div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-foreground">Unable to load dashboard data</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6 md:p-12">
      <div className="container mx-auto max-w-7xl">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-heading font-black uppercase tracking-tight" data-testid="trainer-dashboard-title">
              Trainer Dashboard
            </h1>
            <p className="text-muted-foreground mt-1">Welcome back! Here's your gym overview.</p>
          </div>
          <div className="flex gap-3">
            <Link to="/">
              <Button
                variant="outline"
                className="border-primary text-primary hover:bg-primary hover:text-white"
              >
                Back to Home
              </Button>
            </Link>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="border-destructive text-destructive hover:bg-destructive hover:text-white"
              data-testid="trainer-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-card/50 border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <Users className="w-5 h-5 mr-2 text-primary" />
                Total Members
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold" data-testid="trainer-total-members">
                {dashboardData.total_members}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Active: {dashboardData.active_members}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-card/50 border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <Calendar className="w-5 h-5 mr-2 text-secondary" />
                Today's Visits
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold" data-testid="trainer-today-visits">
                {dashboardData.today_visits}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Members checked in today
              </p>
            </CardContent>
          </Card>

          <Card className="bg-card/50 border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <Dumbbell className="w-5 h-5 mr-2 text-primary" />
                Weekly Workouts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold" data-testid="trainer-weekly-workouts">
                {dashboardData.recent_workouts}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Logged in last 7 days
              </p>
            </CardContent>
          </Card>

          <Card className="bg-card/50 border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <Activity className="w-5 h-5 mr-2 text-secondary" />
                Active Rate
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">
                {dashboardData.total_members > 0 
                  ? Math.round((dashboardData.active_members / dashboardData.total_members) * 100) 
                  : 0}%
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Members with active subscriptions
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Link to="/fitness">
            <Card className="bg-gradient-to-br from-primary/20 to-primary/5 border-primary/30 hover:border-primary transition-all cursor-pointer h-full">
              <CardContent className="pt-6">
                <Activity className="w-8 h-8 text-primary mb-2" />
                <p className="font-heading font-bold uppercase text-lg">Fitness Tracker</p>
                <p className="text-sm text-muted-foreground">Log your own workouts</p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/profile">
            <Card className="bg-gradient-to-br from-secondary/20 to-secondary/5 border-secondary/30 hover:border-secondary transition-all cursor-pointer h-full">
              <CardContent className="pt-6">
                <Users className="w-8 h-8 text-secondary mb-2" />
                <p className="font-heading font-bold uppercase text-lg">My Profile</p>
                <p className="text-sm text-muted-foreground">Manage your trainer profile</p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/store">
            <Card className="bg-gradient-to-br from-primary/20 to-primary/5 border-primary/30 hover:border-primary transition-all cursor-pointer h-full">
              <CardContent className="pt-6">
                <Dumbbell className="w-8 h-8 text-primary mb-2" />
                <p className="font-heading font-bold uppercase text-lg">Gym Store</p>
                <p className="text-sm text-muted-foreground">Browse equipment & supplements</p>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Recent Members */}
        <Card className="bg-card/50 border-white/10">
          <CardHeader>
            <CardTitle className="font-heading uppercase flex items-center">
              <Clock className="w-5 h-5 mr-2" />
              Recent Members
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dashboardData.recent_members.length === 0 ? (
              <p className="text-muted-foreground">No members yet</p>
            ) : (
              <div className="space-y-3">
                {dashboardData.recent_members.map((member) => (
                  <div
                    key={member.id}
                    className="flex justify-between items-center border-b border-white/10 pb-3"
                    data-testid="trainer-member-row"
                  >
                    <div>
                      <p className="font-semibold">{member.full_name}</p>
                      <p className="text-sm text-muted-foreground">{member.email}</p>
                    </div>
                    <div className="text-right">
                      <Badge
                        variant={member.subscription_status === 'active' ? 'default' : 'secondary'}
                        className={member.subscription_status === 'active' ? 'bg-green-500' : ''}
                      >
                        {member.subscription_status}
                      </Badge>
                      {member.subscription_plan && (
                        <p className="text-xs text-muted-foreground mt-1">{member.subscription_plan}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TrainerDashboard;
